import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package, Copy } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DetailField } from '@/components/common/DetailField';
import { StatusBadge } from '@/components/common/StatusBadge';
import { fmt } from '@/lib/format';
import { inventoryService, warehousesService } from '@/services/inventory';
import type { FinishedGood, FinishedGoodStatus } from '@/types/finished-goods';

interface Props {
  item: FinishedGood | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_LOWER: Record<FinishedGoodStatus, string> = {
  AVAILABLE: 'available',
  RESERVED: 'reserved',
  DISPATCHED: 'dispatched',
  RETURNED: 'returned',
  GOOD: 'restocked',
  BAD: 'rejected',
  WRITE_OFF: 'archived',
};

export function FinishedGoodDetailSheet({ item, open, onOpenChange }: Props) {
  // Resolve the parent SKU + warehouse name (BE row sometimes lacks the
  // nested includes; fall back to the catalog list).
  const itemsQuery = useQuery({
    queryKey: ['inventory', 'items', { forFGDetail: true }],
    queryFn: () => inventoryService.list({ pageSize: 200 }),
    staleTime: 60_000,
    enabled: open,
  });
  const warehousesQuery = useQuery({
    queryKey: ['inventory', 'warehouses'],
    queryFn: () => warehousesService.list(),
    staleTime: 60_000,
    enabled: open,
  });

  const parentItem = useMemo(
    () => item?.item ?? itemsQuery.data?.items.find((i) => i.id === item?.itemId),
    [item, itemsQuery.data],
  );
  const warehouse = useMemo(
    () => item?.warehouse ?? warehousesQuery.data?.find((w) => w.id === item?.warehouseId),
    [item, warehousesQuery.data],
  );

  const copyBarcode = async () => {
    if (!item) return;
    try {
      await navigator.clipboard.writeText(item.barcode);
      toast.success('Barcode copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[88vh] w-full max-w-2xl flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-6 pt-5 pb-4">
            <DialogTitle className="sr-only">{item?.barcode ?? 'Finished good unit'}</DialogTitle>
            {item && (
              <div className="flex items-start gap-3">
                <span aria-hidden="true" className="grid h-11 w-11 shrink-0 place-items-center rounded-md bg-success/10 text-success">
                  <Package className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <h2 className="text-[16px] font-semibold leading-tight tracking-tight text-foreground">
                      {parentItem?.name ?? 'Unknown SKU'}
                    </h2>
                    {parentItem && (
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {parentItem.sku}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <StatusBadge status={STATUS_LOWER[item.status]} />
                    {warehouse && (
                      <Badge variant="muted" className="text-[10px]">
                        <span className="font-mono">{warehouse.code}</span>
                        <span className="ml-1">{warehouse.name}</span>
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogHeader>
          <Separator />
          <ScrollArea className="flex-1">
            <div className="space-y-5 px-6 py-5">
              <div className="rounded-md border border-border/60 bg-muted/30 px-4 py-3">
                <div className="text-[10.5px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
                  Per-unit barcode
                </div>
                <div className="mt-1 flex items-center justify-between gap-3">
                  <span className="font-mono text-[15px] text-foreground">{item?.barcode ?? '—'}</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="sm" onClick={copyBarcode}>
                        <Copy className="h-3.5 w-3.5" />
                        Copy
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Copy to clipboard</TooltipContent>
                  </Tooltip>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <DetailField
                  label="Status"
                  value={item ? <StatusBadge status={STATUS_LOWER[item.status]} /> : '—'}
                />
                <DetailField label="Received" value={fmt.dateTime(item?.createdAt)} />
                <DetailField label="Updated" value={fmt.relative(item?.updatedAt)} />
                <DetailField label="Warehouse" value={warehouse?.name ?? '—'} />
                <DetailField label="SKU" value={parentItem?.sku ?? '—'} />
                <DetailField
                  label="UoM"
                  value={(parentItem as { unitOfMeasure?: string } | undefined)?.unitOfMeasure ?? '—'}
                />
              </div>

              {item?.returnOrderItemId && (
                <>
                  <Separator />
                  <DetailField
                    label="Returned via"
                    value={<span className="font-mono text-[12px]">{item.returnOrderItemId}</span>}
                  />
                </>
              )}

              {item?.metadata && Object.keys(item.metadata).length > 0 && (
                <>
                  <Separator />
                  <div>
                    <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                      Metadata
                    </div>
                    <pre className="overflow-x-auto rounded-md bg-muted/40 p-3 text-[11px] text-foreground">
                      {JSON.stringify(item.metadata, null, 2)}
                    </pre>
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
          <div className="flex items-center justify-between gap-2 border-t border-border/60 bg-muted/30 px-4 py-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" disabled>Print label</Button>
              </TooltipTrigger>
              <TooltipContent>Print system not yet wired</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button size="sm" disabled>Allocate to order</Button>
              </TooltipTrigger>
              <TooltipContent>Allocation flow lands with the orders → dispatch pass</TooltipContent>
            </Tooltip>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
